import { c as _c } from "react/compiler-runtime";
/* eslint-disable react/jsx-props-no-spreading */
import type { AuthProviderContext, AuthProviderProps } from '@newfront-insurance/next-auth';
import type { Provider } from '@newfront-insurance/react-provision';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import type { JSX } from 'react';
import * as React from 'react';

// eslint-disable-next-line import/no-cycle
import { HeartbeatProvider } from './heartbeat';
import type { SegmentClient, SegmentClientOptions } from './internals/client';
import { SegmentClientProvider, useSegmentClientContext } from './internals/client-provider';
import { RouteTracker } from './internals/route-tracker';
import { UserTracker } from './internals/user-tracker';
import type { AnalyticsUser, Properties } from './types';

/**
 * These are the traits we want to exist on every tracked user.
 */
interface BaseTraits extends Properties {
  email: string;
  legacyId?: number;
  avatar?: string;
}
interface BaseProps<UserTraits> extends SegmentClientOptions {
  children: React.ReactNode;
  user?: AnalyticsUser<UserTraits>;
  apiKey: string;
  router?: NextRouter;
  appName: string;
  heartbeatsEnabled?: boolean;
  defaultPageProperties?: Properties;
  authProvider: Provider<AuthProviderContext, AuthProviderProps>;
  flags?: Record<string, boolean>;
  enabled?: boolean;
}
interface PropsWithRouter<UserTraits> extends BaseProps<UserTraits> {
  router: NextRouter;
}

/**
 * Adds the Segment client provider, tracks Next.js route changes, and identifies the current user.
 */
export function AnalyticsProvider(props) {
  const $ = _c(7);
  const {
    children,
    router
  } = props;
  if (router) {
    let t0;
    if ($[0] !== children || $[1] !== props || $[2] !== router) {
      t0 = <AnalyticsProviderWithRouter {...props} router={router}>{children}</AnalyticsProviderWithRouter>;
      $[0] = children;
      $[1] = props;
      $[2] = router;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    return t0;
  }
  let t0;
  if ($[4] !== children || $[5] !== props) {
    t0 = <LegacyAnalyticsProvider {...props}>{children}</LegacyAnalyticsProvider>;
    $[4] = children;
    $[5] = props;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  return t0;
}

/**
 * This is the real analytics provider, but it's wrapped for backwards compatibility.
 */
function AnalyticsProviderWithRouter(props) {
  const $ = _c(27);
  let apiKey;
  let appName;
  let children;
  let clientOptions;
  let defaultPageProperties;
  let flags;
  let router;
  let t0;
  let user;
  if ($[0] !== props) {
    const {
      children: t1,
      user: t2,
      apiKey: t3,
      router: t4,
      appName: t5,
      heartbeatsEnabled: t6,
      defaultPageProperties: t7,
      authProvider: _authProvider,
      flags: t8,
      ...t9
    } = props;
    children = t1;
    user = t2;
    apiKey = t3;
    router = t4;
    appName = t5;
    t0 = t6;
    defaultPageProperties = t7;
    flags = t8;
    clientOptions = t9;
    $[0] = props;
    $[1] = apiKey;
    $[2] = appName;
    $[3] = children;
    $[4] = clientOptions;
    $[5] = defaultPageProperties;
    $[6] = flags;
    $[7] = router;
    $[8] = t0;
    $[9] = user;
  } else {
    apiKey = $[1];
    appName = $[2];
    children = $[3];
    clientOptions = $[4];
    defaultPageProperties = $[5];
    flags = $[6];
    router = $[7];
    t0 = $[8];
    user = $[9];
  }
  const heartbeatsEnabled = t0 === undefined ? false : t0;
  const t1 = children as JSX.Element;
  let t2;
  if ($[10] !== appName || $[11] !== flags || $[12] !== heartbeatsEnabled || $[13] !== t1) {
    t2 = <HeartbeatProvider appName={appName} enabled={heartbeatsEnabled} flags={flags}>{t1}</HeartbeatProvider>;
    $[10] = appName;
    $[11] = flags;
    $[12] = heartbeatsEnabled;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== appName || $[16] !== defaultPageProperties || $[17] !== router || $[18] !== t2) {
    t3 = <RouteTracker router={router} appName={appName} defaultPageProperties={defaultPageProperties}>{t2}</RouteTracker>;
    $[15] = appName;
    $[16] = defaultPageProperties;
    $[17] = router;
    $[18] = t2;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== t3 || $[21] !== user) {
    t4 = <UserTracker user={user}>{t3}</UserTracker>;
    $[20] = t3;
    $[21] = user;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== apiKey || $[24] !== clientOptions || $[25] !== t4) {
    t5 = <SegmentClientProvider apiKey={apiKey} clientOptions={clientOptions}>{t4}</SegmentClientProvider>;
    $[23] = apiKey;
    $[24] = clientOptions;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  return t5;
}

/**
 * Wrap the real analytics provider and grab the router the way we used to.
 */
function LegacyAnalyticsProvider(props) {
  const $ = _c(4);
  const {
    children
  } = props;
  const router = useRouter();
  let t0;
  if ($[0] !== children || $[1] !== props || $[2] !== router) {
    t0 = <AnalyticsProviderWithRouter {...props} router={router}>{children}</AnalyticsProviderWithRouter>;
    $[0] = children;
    $[1] = props;
    $[2] = router;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}

/**
 * Access the Segment client directly.
 */
export function useAnalytics() {
  const {
    client
  } = useSegmentClientContext();
  return client;
}